import type { Base } from "./_config/index.js"
import { Config } from "./_config/index.js"
import { Transaction } from "./ethereum/Models/Transaction.js"
import { WalletState } from "./ethereum/Models/WalletState.js"
import { type ConnectEthereumUseCaseInput } from "./ethereum/UseCases/ConnectEthereumUseCase.js"
import { type WaitForTransactionEthereumUseCaseInput } from "./ethereum/UseCases/WaitForTransactionEthereumUseCase.js"
import { type RegisterEthereumUseCaseInput } from "./ethereum/UseCases/RegisterEthereumUseCase.js"
import { type LoginEthereumUseCaseInput } from "./ethereum/UseCases/LoginEthereumUseCase.js"
import { type GetTransactionsEthereumUseCaseOutput } from "./ethereum/UseCases/GetTransactionsEthereumUseCase.js"
import { type GetPoapListUseCaseInput, type GetPoapListUseCaseOutput } from "./poap/UseCases/GetPoapListUseCase.js"
import {
  type GetUserPoapListUseCaseInput,
  type GetUserPoapListUseCaseOutput,
} from "./poap/UseCases/GetUserPoapListUseCase.js"
import { type MintPoapUseCaseInput, type MintPoapUseCaseOutput } from "./poap/UseCases/MintPoapUseCase.js"
import { type GetPoapByIdUseCaseInput, type GetPoapByIdUseCaseOutput } from "./poap/UseCases/GetPoapByIdUseCase.js"

export class Domain {
  #config: Config

  static create(config: Base) {
    return new Domain(config)
  }

  constructor(config: Base) {
    this.#config = Config.create(config)
  }

  get config() {
    return this.#config
  }

  /** Ethereum */
  get InitPublicClientEthereumUseCase () { return this.#getter<void, void>(async () => import("./ethereum/UseCases/InitPublicClientEthereumUseCase.js"), "InitPublicClientEthereumUseCase") } // prettier-ignore
  get ConnectEthereumUseCase() { return this.#getter<ConnectEthereumUseCaseInput, WalletState>(async () => import("./ethereum/UseCases/ConnectEthereumUseCase.js"), "ConnectEthereumUseCase") } // prettier-ignore
  get DisconnectEthereumUseCase() { return this.#getter<void, WalletState>(async () => import("./ethereum/UseCases/DisconnectEthereumUseCase.js"), "DisconnectEthereumUseCase") } // prettier-ignore
  get GetWalletStateEthereumUseCase() { return this.#getter<void, WalletState>(async () => import("./ethereum/UseCases/GetWalletStateEthereumUseCase.js"), "GetWalletStateEthereumUseCase") } // prettier-ignore
  get SwitchChainEthereumUseCase() { return this.#getter<void, WalletState>(async () => import("./ethereum/UseCases/SwitchChainEthereumUseCase.js"), "SwitchChainEthereumUseCase") } // prettier-ignore
  get ReconnectEthereumUseCase() { return this.#getter<void, WalletState>(async () => import("./ethereum/UseCases/ReconnectEthereumUseCase.js"), "ReconnectEthereumUseCase") } // prettier-ignore
  get GetTransactionsEthereumUseCase() { return this.#getter<void, GetTransactionsEthereumUseCaseOutput>(async () => import("./ethereum/UseCases/GetTransactionsEthereumUseCase.js"), "GetTransactionsEthereumUseCase") } // prettier-ignore
  get WaitForTransactionEthereumUseCase() { return this.#getter<WaitForTransactionEthereumUseCaseInput, Transaction>(async () => import("./ethereum/UseCases/WaitForTransactionEthereumUseCase.js"), "WaitForTransactionEthereumUseCase") } // prettier-ignore
  get RegisterEthereumUseCase() { return this.#getter<RegisterEthereumUseCaseInput, WalletState>(async () => import("./ethereum/UseCases/RegisterEthereumUseCase.js"), "RegisterEthereumUseCase") } // prettier-ignore
  get LoginEthereumUseCase() { return this.#getter<LoginEthereumUseCaseInput, WalletState>(async () => import("./ethereum/UseCases/LoginEthereumUseCase.js"), "LoginEthereumUseCase") } // prettier-ignore
  get CleanTransactionsEthereumUseCase() { return this.#getter<void, void>(async () => import("./ethereum/UseCases/CleanTransactionsEthereumUseCase.js"), "CleanTransactionsEthereumUseCase") } // prettier-ignore

  /** Poap */
  get GetPoapListUseCase() { return this.#getter<GetPoapListUseCaseInput, GetPoapListUseCaseOutput>(async () => import("./poap/UseCases/GetPoapListUseCase.js"), "GetPoapListUseCase") } // prettier-ignore
  get GetUserPoapListUseCase() { return this.#getter<GetUserPoapListUseCaseInput, GetUserPoapListUseCaseOutput>(async () => import("./poap/UseCases/GetUserPoapListUseCase.js"), "GetUserPoapListUseCase") } // prettier-ignore
  get MintPoapUseCase() { return this.#getter<MintPoapUseCaseInput, MintPoapUseCaseOutput>(async () => import("./poap/UseCases/MintPoapUseCase.js"), "MintPoapUseCase") } // prettier-ignore
  get GetPoapByIdUseCase() { return this.#getter<GetPoapByIdUseCaseInput, GetPoapByIdUseCaseOutput>(async () => import("./poap/UseCases/GetPoapByIdUseCase.js"), "GetPoapByIdUseCase") } // prettier-ignore

  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
  #getter<I, O>(loader: Function, name: string) {
    return {
      execute: async (input?: I): Promise<O> => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        const Klass = await loader().then((mod) => mod[name])
        const uc = Klass.create({ config: this.#config })

        return uc.execute(input) as O
      },
    }
  }
}
